import type { ReactElement } from 'react';
import Head from 'next/head';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { BspProvider } from '@eventbrite/bsp-sdk/react';
import { FavIconLinks } from '@eventbrite/webapp-public-content';
import type { Stage } from '@eventbrite/webapp-constants';

import { ErrorBoundary } from '@/src/homepage/components/ErrorBoundary';
import { GlobalFallback } from '@/src/homepage/components/ErrorBoundary/GlobalFallback';
import { RoktInit } from '@/src/homepage/components/RoktInit/RoktInit';
import { useBspConfig } from '@/src/homepage/bsp/config';
import { wrapper } from '@/src/homepage/redux/store';
import { SurfaceConsentScripts } from '@/shared/consent/SurfaceConsentScripts';
import '@/src/homepage/styles/homepageGlobal.module.scss';

export interface HomepagePageProps {
  stage: Stage;
  isHomepageRoute?: boolean;
  i18nProps?: { locale?: string; messages?: Record<string, string> };
  initialState?: unknown;
  [key: string]: unknown;
}

/**
 * The homepage surface's per-page shell. Mounted from each homepage page's
 * `getLayout`, so pages outside `/home` are unaffected.
 *
 * The standalone app built these providers in `_app` from its
 * `getInitialProps` (stage, env, split values, redux initial state). The host
 * has no surface `_app`, so the same server data now rides each page's
 * `getServerSideProps`: `initialState` is hydrated by `next-redux-wrapper`'s
 * `useWrappedStore` (the element's `initialState` prop is read as the wrapper's
 * hydration payload), and `stage`/`i18nProps` come from the page element too.
 *
 * The side-effect import of `homepageGlobal.module.scss` is what pulls the
 * scoped legacy-EDS chunk into the homepage route graph only (never the host's
 * shared `_app` chunk); `_document` renders the matching wrapper class on
 * homepage documents. See docs/consolidation/folds/homepage-fold-decisions.md D1.
 */
export function HomepageLayout({ page, route }: { page: ReactElement; route: string }) {
  const pageProps = page.props as HomepagePageProps;
  const { stage, i18nProps, isHomepageRoute } = pageProps;
  const bspConfig = useBspConfig(stage);

  const { store } = wrapper.useWrappedStore(page.props);

  return (
    <>
      <Head>
        <FavIconLinks stage={stage} />
      </Head>
      {isHomepageRoute ? <RoktInit stage={stage} /> : null}
      <BspProvider config={bspConfig}>
        <IntlProvider
          locale={i18nProps?.locale || 'en'}
          messages={i18nProps?.messages || {}}
        >
          <Provider store={store}>
            <ErrorBoundary componentName="App" fallback={GlobalFallback} resetKey={route}>
              {page}
            </ErrorBoundary>
          </Provider>
        </IntlProvider>
      </BspProvider>
      <SurfaceConsentScripts />
    </>
  );
}
